import { AxiosError } from 'axios';

/** Celebrate's rejection shape: one entry per request segment it validated. */
interface ValidationPayload {
  message?: string;
  validation?: Record<string, { keys?: string[]; message?: string } | undefined>;
}

/**
 * What celebrate actually said, as opposed to what it leads with.
 *
 * A schema rejection answers `message: 'Validation failed'` — three words that name no field and
 * are not even in Hebrew — with the real reason one level down under `validation`. Showing the top
 * line is how "חנות חדשה" came to fail with nothing on screen to act on. The per-field messages
 * are joined because the validator runs with `abortEarly: false`: when two fields are wrong, the
 * user should fix both in one pass rather than discovering the second after saving.
 */
const validationMessage = (data: ValidationPayload | undefined): string | null => {
  const messages = Object.values(data?.validation ?? {})
    .map(segment => segment?.message?.trim())
    .filter((message): message is string => !!message);

  return messages.length ? messages.join(' · ') : null;
};

/**
 * The Hebrew message the API sends with a rejected write (400 / 409), e.g. deleting a
 * plan that still has subscriptions. Falls back to `fallback` for network/5xx errors,
 * which carry no user-facing copy.
 */
export const apiErrorMessage = (error: unknown, fallback = 'הפעולה נכשלה. נסה שוב.'): string => {
  const data = (error as AxiosError<ValidationPayload>)?.response?.data;
  const message = validationMessage(data) ?? data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

/**
 * True when the write was rejected because somebody else got there first (optimistic locking).
 *
 * Product Hub answers 409 for both "a domain rule forbids this" and "the row moved under you", so
 * the status alone is not enough to decide whether offering a reload makes sense — the stable
 * `code` is. Anything else 409 is a business rule the user has to resolve, not retry.
 */
export const isConflictError = (error: unknown): boolean => apiErrorCode(error) === 'PH_VERSION_CONFLICT';

/**
 * The stable `code` the API sends beside a rejected write, when it sent one.
 *
 * Matching on this rather than on `message`: the Hebrew copy is reworded freely, the code is the
 * contract (see `PH_ERROR_CODES`).
 */
export const apiErrorCode = (error: unknown): string | undefined =>
  (error as AxiosError<{ code?: string }>)?.response?.data?.code;
